import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaStar, FaShoppingBag } from 'react-icons/fa';
import './Hero.css';

// Hero image is in public folder
const heroImage = '/Hero 1.webp';

const Hero = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const handleOrderNow = (e) => {
    e.preventDefault();
    navigate('/');
    // Set hash after navigation to trigger Home component's hash handling
    setTimeout(() => {
      window.location.hash = '#products';
    }, 0);
  };

  return (
    <section className="hero" id="home">
      <div className="floating-elements">
        {/* Animated Butterflies */}
        <motion.div
          className="floating-butterfly butterfly-1"
          animate={{
            x: [0, 50, -30, 40, 0],
            y: [0, -40, -60, -30, 0],
            rotate: [0, 15, -10, 20, 0],
            scale: [1, 1.1, 0.9, 1.05, 1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <svg width="40" height="40" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(50, 50)">
              <ellipse cx="-15" cy="0" rx="20" ry="30" fill="#FFB6C1" opacity="0.8" transform="rotate(-20)" />
              <ellipse cx="15" cy="0" rx="20" ry="30" fill="#FFB6C1" opacity="0.8" transform="rotate(20)" />
              <ellipse cx="-15" cy="0" rx="15" ry="25" fill="#E91E63" opacity="0.4" transform="rotate(-20)" />
              <ellipse cx="15" cy="0" rx="15" ry="25" fill="#E91E63" opacity="0.4" transform="rotate(20)" />
              <circle cx="0" cy="0" r="3" fill="#36454F" />
            </g>
          </svg>
        </motion.div>

        <motion.div
          className="floating-butterfly butterfly-2"
          animate={{
            x: [0, -60, 40, -20, 0],
            y: [0, -50, -70, -40, 0],
            rotate: [0, -20, 15, -25, 0],
            scale: [1, 0.95, 1.15, 0.9, 1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          <svg width="35" height="35" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(50, 50)">
              <ellipse cx="-12" cy="0" rx="18" ry="28" fill="#FFB6C1" opacity="0.7" transform="rotate(-25)" />
              <ellipse cx="12" cy="0" rx="18" ry="28" fill="#FFB6C1" opacity="0.7" transform="rotate(25)" />
              <ellipse cx="-12" cy="0" rx="12" ry="22" fill="#E91E63" opacity="0.3" transform="rotate(-25)" />
              <ellipse cx="12" cy="0" rx="12" ry="22" fill="#E91E63" opacity="0.3" transform="rotate(25)" />
              <circle cx="0" cy="0" r="2.5" fill="#36454F" />
            </g>
          </svg>
        </motion.div>

        <motion.div
          className="floating-butterfly butterfly-3"
          animate={{
            x: [0, 30, -50, 25, 0],
            y: [0, -60, -80, -50, 0],
            rotate: [0, 25, -15, 30, 0],
            scale: [1, 1.2, 0.85, 1.1, 1]
          }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        >
          <svg width="45" height="45" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(50, 50)">
              <ellipse cx="-18" cy="0" rx="22" ry="32" fill="#FFB6C1" opacity="0.75" transform="rotate(-15)" />
              <ellipse cx="18" cy="0" rx="22" ry="32" fill="#FFB6C1" opacity="0.75" transform="rotate(15)" />
              <ellipse cx="-18" cy="0" rx="17" ry="27" fill="#E91E63" opacity="0.35" transform="rotate(-15)" />
              <ellipse cx="18" cy="0" rx="17" ry="27" fill="#E91E63" opacity="0.35" transform="rotate(15)" />
              <circle cx="0" cy="0" r="3.5" fill="#36454F" />
            </g>
          </svg>
        </motion.div>

        <motion.div
          className="floating-butterfly butterfly-4"
          animate={{
            x: [0, -40, 60, -35, 0],
            y: [0, -45, -65, -35, 0],
            rotate: [0, -18, 12, -22, 0],
            scale: [1, 0.9, 1.1, 0.95, 1]
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
        >
          <svg width="38" height="38" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(50, 50)">
              <ellipse cx="-14" cy="0" rx="19" ry="29" fill="#FFB6C1" opacity="0.8" transform="rotate(-22)" />
              <ellipse cx="14" cy="0" rx="19" ry="29" fill="#FFB6C1" opacity="0.8" transform="rotate(22)" />
              <ellipse cx="-14" cy="0" rx="14" ry="24" fill="#E91E63" opacity="0.4" transform="rotate(-22)" />
              <ellipse cx="14" cy="0" rx="14" ry="24" fill="#E91E63" opacity="0.4" transform="rotate(22)" />
              <circle cx="0" cy="0" r="3" fill="#36454F" />
            </g>
          </svg>
        </motion.div>

        {/* Keep some sparkles and hearts for variety */}
        <motion.div
          className="floating-sparkle"
          animate={{
            y: [0, -30, 0],
            rotate: [0, -5, 5, 0],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        >
          ✨
        </motion.div>
        <motion.div
          className="floating-heart"
          animate={{
            y: [0, -25, 0],
            rotate: [0, -3, 3, 0],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
        >
          💖
        </motion.div>
      </div>

      <div className="hero-container">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="hero-title">Handmade Haven</h1>
          <p className="hero-tagline">
            <span className="heart-icon">🦋</span>
            Handcrafted With Love & Magic
            <span className="heart-icon">💖</span>
          </p>
          <p className="hero-description">
            Every piece is a work of art, created with premium materials, intricate detailing, and pure creativity by Muskan Kazi.
          </p>
          <p className="hero-location-text">
            Handmade Haven is a premium resin art supply store based in Ahmedabad, Gujarat. Fast shipping across India.
          </p>
          <div className="hero-buttons">
            <motion.a
              href="#products"
              onClick={(e) => { e.preventDefault(); scrollToSection('products'); }}
              className="btn btn-primary"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaStar />
              Explore Creations
            </motion.a>
            <motion.a
              href="#products"
              onClick={handleOrderNow}
              className="btn btn-secondary"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaShoppingBag />
              Order Now
            </motion.a>
          </div>
        </motion.div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="featured-product-card">
            <div className="product-label-top">
              <img src="/Handmade haven.png" alt="Handmade Haven" className="product-logo-small" loading="lazy" />
              <span>Handmade Haven</span>
            </div>
            <div className="product-image">
              <img src={heroImage} alt="Handmade Haven Resin" loading="lazy" />
            </div>
            <div className="product-tag">
              Made with Love
            </div>
            <div className="feature-cards">
              <motion.div
                className="feature-card"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                      fill="#FFB6C1" stroke="#E91E63" strokeWidth="1.5" />
                  </svg>
                </div>
                <p>Unique Designs</p>
              </motion.div>
              <motion.div
                className="feature-card"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                      fill="none" stroke="#E91E63" strokeWidth="1.5" />
                  </svg>
                </div>
                <p>Premium Quality</p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;

